import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { resolveMedia, publicApi } from "../../lib/api";

const LINKS = [
  { label: "HOME", href: "#hero" },
  { label: "ABOUT US", href: "#positioning" },
  { label: "SERVICES", href: "#services" },
  { label: "SOLUTIONS", href: "#solutions" },
  { label: "WORK", href: "#work" },
  { label: "INDUSTRIES", href: "#industries" },
  { label: "EVENTS", href: "/events", isRoute: true },
  { label: "INSIGHTS", href: "#process" },
  { label: "CONTACT US", href: "#contact" },
];

export default function Navbar({ logo }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("#hero");
  const [hasNewEvent, setHasNewEvent] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    publicApi
      .get("/events/new-status")
      .then((res) => {
        if (mounted && res.data && res.data.has_new) {
          setHasNewEvent(true);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);

      if (location.pathname === "/") {
        const sections = LINKS.filter((l) => !l.isRoute).map((l) => l.href.substring(1));
        const scrollPos = window.scrollY + 120;
        for (let i = sections.length - 1; i >= 0; i--) {
          const el = document.getElementById(sections[i]);
          if (el && el.offsetTop <= scrollPos) {
            setActiveSection(`#${sections[i]}`);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && open) setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const handleNavClick = (e, href) => {
    setOpen(false);
    if (href.startsWith("#") && location.pathname === "/") {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, "", href);
        setActiveSection(href);
      }
    }
  };

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed left-1/2 z-50 w-[94%] max-w-7xl -translate-x-1/2 rounded-full border border-black/5 bg-white/95 px-4 py-2.5 backdrop-blur-md transition-all duration-300 md:px-6 md:py-3 ${
          scrolled ? "top-3 shadow-[0_10px_40px_-12px_rgba(11,19,43,0.35)]" : "top-4 md:top-5 shadow-lg"
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/#hero"
            onClick={(e) => handleNavClick(e, "#hero")}
            data-testid="nav-logo"
            className="flex shrink-0 items-center"
          >
            <img
              src={resolveMedia(logo || "/admall-logo.png")}
              alt="ADMALL INDIA"
              className="h-12 w-auto object-contain sm:h-14 lg:h-16"
            />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex">
            {LINKS.map((item) => {
              const testId = `nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
              const active = item.isRoute
                ? location.pathname === item.href
                : location.pathname === "/" && activeSection === item.href;
              const href = item.isRoute
                ? item.href
                : location.pathname === "/"
                ? item.href
                : `/${item.href}`;

              if (item.isRoute) {
                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    data-testid={testId}
                    className={`inline-flex items-center font-body text-[13px] tracking-wide transition-colors ${
                      active ? "font-bold text-[#FF5722]" : "font-semibold text-navy/80 hover:text-[#FF5722]"
                    }`}
                  >
                    <span>{item.label}</span>
                    {item.label === "EVENTS" && hasNewEvent && (
                      <span
                        data-testid="nav-events-new-badge"
                        className="ml-1.5 inline-flex items-center rounded-full bg-[#FF5722] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-white shadow-sm"
                        aria-label="New events available"
                      >
                        NEW
                      </span>
                    )}
                  </Link>
                );
              }

              return (
                <a
                  key={item.label}
                  href={href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  data-testid={testId}
                  className={`font-body text-[13px] tracking-wide transition-colors ${
                    active ? "font-bold text-[#FF5722]" : "font-semibold text-navy/80 hover:text-[#FF5722]"
                  }`}
                >
                  {item.label}
                </a>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={location.pathname === "/" ? "#contact" : "/#contact"}
              onClick={(e) => handleNavClick(e, "#contact")}
              data-testid="nav-cta-consultation"
              className="hidden items-center gap-2 rounded-full bg-[#FF5722] px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-[#E64A19] md:flex shadow-sm hover:shadow-orange-500/20"
            >
              Book Free Consultation <ArrowRight size={16} />
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-nav-overlay"
              className="flex h-11 w-11 items-center justify-center rounded-full bg-navy text-white xl:hidden"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay with backdrop dismissal */}
      <div
        id="mobile-nav-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile Navigation"
        data-testid="mobile-nav-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
        className={`fixed inset-0 z-40 bg-navy transition-all duration-500 xl:hidden ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          className="flex h-full flex-col justify-center gap-2 px-8 pt-20"
        >
          {LINKS.map((item, i) => {
            const testId = `mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`;
            const active = item.isRoute
              ? location.pathname === item.href
              : location.pathname === "/" && activeSection === item.href;
            const href = item.isRoute
              ? item.href
              : location.pathname === "/"
              ? item.href
              : `/${item.href}`;

            if (item.isRoute) {
              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={testId}
                  className={`inline-flex items-center font-display text-4xl font-extrabold uppercase tracking-tight transition-transform hover:translate-x-2 ${
                    active ? "text-[#FF5722]" : "text-white/90 hover:text-[#FF5722]"
                  }`}
                  style={{ transitionDelay: open ? `${i * 30}ms` : "0ms" }}
                >
                  <span>{item.label}</span>
                  {item.label === "EVENTS" && hasNewEvent && (
                    <span className="ml-3 inline-flex items-center rounded-full bg-[#FF5722] px-2 py-0.5 text-xs font-black uppercase tracking-wider text-white">
                      NEW
                    </span>
                  )}
                </Link>
              );
            }

            return (
              <a
                key={item.label}
                href={href}
                onClick={(e) => handleNavClick(e, item.href)}
                data-testid={testId}
                className={`font-display text-4xl font-extrabold uppercase tracking-tight transition-transform hover:translate-x-2 ${
                  active ? "text-[#FF5722]" : "text-white/90 hover:text-[#FF5722]"
                }`}
                style={{ transitionDelay: open ? `${i * 30}ms` : "0ms" }}
              >
                {item.label}
              </a>
            );
          })}
          <a
            href={location.pathname === "/" ? "#contact" : "/#contact"}
            onClick={(e) => handleNavClick(e, "#contact")}
            className="mt-6 inline-flex w-fit items-center gap-2 rounded-full bg-[#FF5722] px-6 py-3 font-display text-base font-bold uppercase tracking-wider text-white"
          >
            Book Free Consultation <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </>
  );
}
